import styled from 'styled-components';
import { Button } from '@shared/ui/Button';
import { fleetLeading, fleetType } from '@entities/fleet';
import { NBSP, type HoursAnomaly } from '@entities/chronum';
import { NARROW, toneColor } from './ui';

const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  overflow: hidden;
`;

const Item = styled.li<{ $tone: 'danger' | 'warning' }>`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: start;
  gap: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(4)}`};
  padding: ${({ theme }) => `${theme.spacing(3.5)} ${theme.spacing(4)}`};
  border-left: 3px solid ${({ theme, $tone }) => toneColor(theme, $tone)};

  & + & {
    border-top: 1px solid ${({ theme }) => theme.colors.border};
  }

  & .tag {
    display: inline-block;
    margin-right: ${({ theme }) => theme.spacing(2)};
    font-size: ${fleetType.micro};
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  & p {
    margin: 0;
    font-size: ${fleetType.base};
    line-height: ${fleetLeading.normal};
    color: ${({ theme }) => theme.colors.text.primary};
    text-wrap: pretty;
  }

  & ul {
    margin: ${({ theme }) => `${theme.spacing(1.5)} 0 0`};
    padding-left: ${({ theme }) => theme.spacing(5)};
    font-size: ${fleetType.body};
    line-height: ${fleetLeading.normal};
    color: ${({ theme }) => theme.colors.text.secondary};
    font-variant-numeric: tabular-nums;
  }

  @container chronum (max-width: ${NARROW}) {
    grid-template-columns: minmax(0, 1fr);
    padding: ${({ theme }) => theme.spacing(3)};

    & > button {
      justify-self: start;
    }
  }
`;

interface Props {
  items: readonly HoursAnomaly[];
  canWrite: boolean;
  onOpenSite: (siteId: string) => void;
  onOpenUnmapped: () => void;
}

function actionLabel(a: HoursAnomaly, canWrite: boolean): { text: string; aria: string } | null {
  switch (a.kind) {
    case 'conflict':
      return canWrite
        ? { text: 'Исправить', aria: `Исправить привязку площадки «${a.siteName}»` }
        : { text: 'Открыть площадку', aria: `Открыть площадку «${a.siteName}»` };
    case 'unbound':
      return canWrite
        ? { text: 'Привязать', aria: `Привязать площадку «${a.siteName}»` }
        : { text: 'Открыть площадку', aria: `Открыть площадку «${a.siteName}»` };
    case 'unmapped':
      return canWrite
        ? { text: 'Выбрать категории', aria: 'Выбрать категории ФОТ должностям без категории' }
        : { text: 'Открыть должности', aria: 'Открыть должности без категории ФОТ' };
    default:
      return null;
  }
}

/** Аномалии свода: что не так, по-русски, и переход туда, где это исправить. */
export function AnomalyList({ items, canWrite, onOpenSite, onOpenUnmapped }: Props) {
  return (
    <List>
      {items.map((a) => {
        const action = actionLabel(a, canWrite);
        return (
          <Item key={a.id} $tone={a.tone}>
            <div>
              <p>
                <span className="tag">{a.tone === 'danger' ? 'Не вошло в свод' : 'Проверьте'}</span>
                {a.text}
              </p>
              {a.items && a.items.length > 0 && (
                <ul>
                  {a.items.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                  {a.more ? <li>{`и${NBSP}ещё ${a.more}`}</li> : null}
                </ul>
              )}
            </div>
            {action && (
              <Button
                type="button"
                $variant="secondary"
                $size="sm"
                aria-label={action.aria}
                onClick={() => {
                  if (a.kind === 'conflict' || a.kind === 'unbound') onOpenSite(a.siteId);
                  else if (a.kind === 'unmapped') onOpenUnmapped();
                }}
              >
                {action.text}
              </Button>
            )}
          </Item>
        );
      })}
    </List>
  );
}
